import React, {useState} from 'react';

import {useCurrentUserQuery} from '@/api';

import {OutlinePrimaryLink} from '../components/OutlinePrimaryLink';

const DropdownItem: React.FC<{href: string; children: React.ReactNode}> = ({
  href,
  children,
}) => (
  <a href={href} className="block px-4 py-2 text-sm hover:bg-neutral-100">
    {children}
  </a>
);

export const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const {data} = useCurrentUserQuery({errorPolicy: 'ignore'});
  const isLoggedIn = !!data?.currentUser;
  const user = data?.currentUser;

  return (
    <header className="sticky top-0 z-50 h-20 bg-white shadow-sm">
      <div className="mx-auto flex h-full max-w-screen-xl items-center justify-between pl-10 pr-6">
        {/* Logo */}
        <a href="/" aria-label="ACTION CENTER MIHARA">
          <img
            src="/assets/logo-square--black.png"
            alt="ACTION CENTER MIHARA"
            className="h-9 w-auto lg:hidden"
          />
          <img
            src="/assets/logo--black.png"
            alt="ACTION CENTER MIHARA"
            className="hidden h-[18px] w-auto lg:block"
          />
        </a>

        {/* Mobile: login + burger */}
        <div className="flex items-center gap-3 lg:hidden">
          {!isLoggedIn && (
            <OutlinePrimaryLink href="/signin" size="sm">
              新規登録 / ログイン
            </OutlinePrimaryLink>
          )}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex flex-col gap-1 p-1"
            aria-label="メニュー"
            aria-expanded={menuOpen}>
            <span className="block h-0.5 w-6 bg-gray-700" />
            <span className="block h-0.5 w-6 bg-gray-700" />
            <span className="block h-0.5 w-6 bg-gray-700" />
          </button>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-4 lg:flex">
          {/* イベントをさがす */}
          <a
            href="/#events"
            className="flex items-center gap-1 text-base text-gray-700 hover:text-primary">
            <i className="fas fa-search text-xl" aria-hidden="true" />
            イベントをさがす
          </a>

          {/* イベント・グループをつくる */}
          <div className="group relative">
            <button className="flex cursor-pointer items-center gap-1 text-base text-gray-700 hover:text-primary">
              <i className="fas fa-plus-circle text-xl" aria-hidden="true" />
              イベント・グループをつくる
            </button>
            <div className="invisible absolute left-0 top-full z-50 min-w-44 rounded bg-white py-4 text-xl shadow-lg group-hover:visible">
              <DropdownItem href="/mypage/events/new?eventType=event">
                イベントをつくる
              </DropdownItem>
              <DropdownItem href="/mypage/groups/new">
                グループをつくる
              </DropdownItem>
            </div>
          </div>

          {/* スペースをかりる */}
          <a
            href="/mypage/events/new?eventType=place"
            className="flex items-center gap-1 text-base text-gray-700 hover:text-primary">
            <i className="fas fa-building text-xl" aria-hidden="true" />
            スペースをかりる
          </a>

          {/* ACミハラについて */}
          <a
            href="/about"
            className="flex items-center gap-1 text-base text-gray-700 hover:text-primary">
            <i className="far fa-question-circle text-xl" aria-hidden="true" />
            ACミハラについて
          </a>

          {/* User */}
          {isLoggedIn ? (
            <div className="group relative">
              <button className="flex items-center">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.fullName ?? ''}
                    className="size-14 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex size-9 items-center justify-center rounded-full bg-neutral-100 text-sm font-semibold text-neutral-700">
                    {user?.fullName?.[0] ?? '?'}
                  </span>
                )}
              </button>
              <div className="invisible absolute right-0 top-full z-50 min-w-48 rounded bg-white px-2 py-6 shadow-lg group-hover:visible">
                <div className="px-4 py-2 text-sm font-semibold">
                  {user?.fullName}
                </div>
                <div className="px-4 pb-2 text-xs text-neutral-500">
                  {user?.email}
                </div>
                <hr className="my-0.5 border-neutral-100" />
                <DropdownItem href="/mypage/entries?eventStatus=going">
                  参加イベント
                </DropdownItem>
                <DropdownItem href="/mypage/follows">
                  フォロー中のグループ
                </DropdownItem>
                <DropdownItem href="/mypage/events?eventStatus=published">
                  予約の管理
                </DropdownItem>
                <DropdownItem href="/mypage/groups">
                  グループの管理
                </DropdownItem>
                <DropdownItem href="/mypage/account/base">
                  アカウント設定
                </DropdownItem>
                <hr className="my-0.5 border-neutral-100" />
                <a
                  href="/signout"
                  rel="nofollow"
                  className="block px-4 py-2 text-sm hover:bg-neutral-100">
                  ログアウト
                </a>
              </div>
            </div>
          ) : (
            <OutlinePrimaryLink href="/signin" size="sm">
              新規登録 / ログイン
            </OutlinePrimaryLink>
          )}
        </nav>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <nav className="border-t bg-white px-4 pb-4 lg:hidden">
          <a
            href="/#events"
            className="block border-b py-3 text-sm text-gray-700">
            イベントをさがす
          </a>
          <a
            href="/mypage/events/new?eventType=event"
            className="block border-b py-3 text-sm text-gray-700">
            イベントをつくる
          </a>
          <a
            href="/mypage/groups/new"
            className="block border-b py-3 text-sm text-gray-700">
            グループをつくる
          </a>
          <a
            href="/mypage/events/new?eventType=place"
            className="block border-b py-3 text-sm text-gray-700">
            スペースをかりる
          </a>
          <a
            href="/about"
            className="block border-b py-3 text-sm text-gray-700">
            ACミハラについて
          </a>

          {isLoggedIn ? (
            <>
              <a
                href="/mypage/entries?eventStatus=going"
                className="block border-b py-3 text-sm text-gray-700">
                参加イベント
              </a>
              <a
                href="/mypage/follows"
                className="block border-b py-3 text-sm text-gray-700">
                フォロー中のグループ
              </a>
              <a
                href="/mypage/events?eventStatus=published"
                className="block border-b py-3 text-sm text-gray-700">
                予約の管理
              </a>
              <a
                href="/mypage/groups"
                className="block border-b py-3 text-sm text-gray-700">
                グループの管理
              </a>
              <a
                href="/mypage/account/base"
                className="block border-b py-3 text-sm text-gray-700">
                アカウント設定
              </a>
              <a
                href="/signout"
                rel="nofollow"
                className="block py-3 text-sm text-gray-700">
                ログアウト
              </a>
            </>
          ) : (
            <OutlinePrimaryLink href="/signin" size="sm" className="mt-3">
              新規登録 / ログイン
            </OutlinePrimaryLink>
          )}
        </nav>
      )}
    </header>
  );
};
